// Employee onboarding steps (same as old Kinntegra) grouped into tabs.
const f = (key, label, type = "text", req = true, extra = {}) => ({ key, label, type, req, ...extra });
const doc = (key, label, req = false, solo = false) => f(key, label, "file", req, { solo });
const opts = (a) => a.map((o) => ({ value: o, label: o }));

export const DESIGNATIONS = ["Manging Director", "Director", "President", "Vice President", "Assistance Vice President", "Manager", "Sr.Executive", "Executive", "Proprietor"];
const RANK = ["Proprietor", ...DESIGNATIONS.filter((x) => x !== "Proprietor")];
const rank = (d) => (RANK.includes(d) ? RANK.indexOf(d) : 99);
const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const ACCT = ["Savings", "Current", "NRE", "NRO"];
export const DEPARTMENTS = { Admin: ["Subdept"], Operation: ["User Creation", "Customer Engagement", "Settlement Team", "Master", "Holding Report"],
  Sales: ["B2B", "B2C", "B2B&B2C"], Management: ["Business Management"] };
const withCur = (list, cur) => opts(cur && !list.includes(cur) ? [cur, ...list] : list);
const parts = (v) => String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
const subsFor = (depts) => [...new Set(depts.flatMap((d) => DEPARTMENTS[d] || []))];

export const EMP_TITLES = { general: "General Information", details: "Employee Details", photo: "Photo ID Details", address: "Address Details",
  bank: "Bank Details", cert: "Certification" };

export const EMP_TABS = [
  { key: "info", label: "General & Employee Details", steps: ["general", "details"] },
  { key: "id", label: "Photo ID & Address", steps: ["photo", "address"] },
  { key: "bank", label: "Bank & Certification", steps: ["bank", "cert"] },
  { key: "rights", label: "Rights", steps: [], custom: true },
  { key: "downloads", label: "Downloads", steps: [], custom: true },
];

export function empFields(step, ctx) {
  const d = (ctx.profile || {}).address || {};
  const g = (ctx.profile || {}).general || {};
  const org = g.associate_id;
  const sups = [...new Set([...(ctx.associates || []).filter((a) => a.id === org).map((a) => a.name),
    ...(ctx.employees || []).filter((e) => e.associate_id === org && g.designation && rank(e.designation) < rank(g.designation))
      .sort((a, b) => rank(a.designation) - rank(b.designation) || a.name.localeCompare(b.name)).map((e) => e.name)].filter(Boolean))];
  switch (step) {
    case "general": return [
      f("associate_id", "ASSOCIATE", "select", true, { options: (ctx.associates || []).map((a) => ({ value: a.id, label: `${a.name} (${a.code})` })), clears: { supervisor: "" } }),
      f("designation", "DESIGNATION", "select", true, { options: opts(DESIGNATIONS), clears: { supervisor: "" } }),
      f("department", "DEPARTMENT", "multipick", false, { options: [...new Set([...Object.keys(DEPARTMENTS), ...parts(g.department)])],
        patch: (nx) => ({ sub_department: parts(g.sub_department).filter((s) => subsFor(nx).includes(s)).join(", ") }) }),
      f("sub_department", "SUB-DEPARTMENT", "multipick", false, { options: subsFor(parts(g.department)), empty: "Pick a department first" }),
      f("supervisor", "SUPERVISED BY", "select", false, { options: withCur(sups, g.supervisor) }), doc("employment_agreement", "EMPLOYMENT AGREEMENT", false, true)];
    case "details": return [
      f("name", "EMPLOYEE NAME"), f("mobile", "MOBILE NUMBER", "mobile"), f("telephone", "TELEPHONE NUMBER", "phone", false),
      f("email", "EMAIL ID", "email"), f("blood_group", "BLOOD GROUP", "select", false, { options: opts(BLOOD) }),
      f("health_issues", "HEALTH ISSUES (IF ANY)", "text", false),
      f("_em1", "EMERGENCY CONTACT 1", "header", false), f("emergency_name_1", "NAME"), f("emergency_mobile_1", "MOBILE NUMBER", "mobile"),
      f("emergency_email_1", "EMAIL ID", "email", false),
      f("_em2", "EMERGENCY CONTACT 2", "header", false), f("emergency_name_2", "NAME", "text", false),
      f("emergency_mobile_2", "MOBILE NUMBER", "mobile", false), f("emergency_email_2", "EMAIL ID", "email", false)];
    case "photo": return [
      f("pan_number", "PANCARD NUMBER", "pan"), doc("pan_card", "PAN CARD", true), f("aadhaar_number", "AADHAAR NUMBER", "aadhaar", false),
      doc("aadhaar_card", "AADHAAR CARD"), f("dob", "DATE OF BIRTH", "date"), f("doa", "DATE OF ANNIVERSARY", "date", false),
      doc("self_photo", "SELF PHOTO", true, true)];
    case "address": return [
      f("_c", "CORRESPONDENCE ADDRESS", "header", false), f("address1", "ADDRESS 1"), f("address2", "ADDRESS 2"), f("address3", "ADDRESS 3", "text", false),
      f("_address", "", "address", true), doc("address_proof", "CORRESPONDENCE ADDRESS PROOF", true),
      f("same_permanent", "PERMANENT ADDRESS SAME AS CORRESPONDENCE", "checkbox", false),
      ...(d.same_permanent ? [] : [f("_p", "PERMANENT ADDRESS", "header", false), f("p_address1", "ADDRESS 1", "text", false),
        f("p_address2", "ADDRESS 2", "text", false), f("p_address3", "ADDRESS 3", "text", false), f("p_city", "CITY", "text", false),
        f("p_state", "STATE", "text", false), f("p_pincode", "PINCODE", "text", false), doc("p_address_proof", "PERMANENT ADDRESS PROOF")])];
    case "bank": return [
      f("ifsc", "IFSC CODE", "ifsc"), doc("bank_proof", "BANK PROOF", true),
      f("bank_name", "BANK NAME", "text", true, { readOnly: true, lookup: true }), f("branch", "BRANCH", "text", true, { readOnly: true, lookup: true }),
      f("micr", "MICR", "text", false, { readOnly: true }), f("account_type", "ACCOUNT TYPE", "select", true, { options: opts(ACCT) }),
      f("account_number", "ACCOUNT NUMBER", "account")];
    case "cert": return [
      f("_n", "NISM VA", "header", false), f("nism_va_number", "NISM VA CERTIFICATE NUMBER", "text", false), doc("nism_va_proof", "NISM VA CERTIFICATE"),
      f("nism_va_validity", "VALIDITY", "date", false),
      f("_e", "EUIN", "header", false), f("euin_name", "NAME OF EUIN HOLDER", "text", false), f("euin_number", "EUIN NUMBER", "euin", false),
      doc("euin_proof", "EUIN PROOF"), f("euin_validity", "VALIDITY", "date", false),
      f("_d", "DEGREE / CA / CS", "header", false), f("degree_name", "DEGREE NAME", "text", false), f("degree_number", "DEGREE NUMBER", "text", false),
      doc("degree_proof", "DEGREE CERTIFICATE"), f("degree_validity", "VALIDITY", "date", false),
      f("ca_number", "CA NUMBER", "text", false), f("ca_validity", "CA VALIDITY", "date", false),
      f("cs_number", "CS NUMBER", "text", false), f("cs_validity", "CS VALIDITY", "date", false)];
    default: return [];
  }
}

const RX = { email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, mobile: /^[6-9]\d{9}$/, pan: /^[A-Z]{5}\d{4}[A-Z]$/, aadhaar: /^\d{12}$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/, account: /^\d{9,18}$/, euin: /^E?\d{6}$/ };

export function validateEmp(step, data, ctx) {
  const errs = {};
  for (const fl of empFields(step, ctx)) {
    if (fl.type === "header") continue;
    const v = data[fl.key];
    if (fl.type === "address") {
      ["country", "state", "city", "pincode"].forEach((k) => { if (!data[k]) errs[k] = "Required"; });
      continue;
    }
    const empty = v === undefined || v === null || v === "" || (fl.type === "file" && !v?.storage_path);
    if (fl.req && empty) { errs[fl.key] = fl.lookup ? "Enter a valid IFSC to fill this" : "Required"; continue; }
    if (!empty && RX[fl.type] && !RX[fl.type].test(String(v))) errs[fl.key] = "Invalid format";
  }
  return errs;
}
